import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { splitLocale } from '@/lib/seo-data.mjs';
import { COPY, type Copy, type Lang } from './copy';

interface LangContextValue {
  /** The language of the current URL: `/it…` is Italian, every other path English. */
  lang: Lang;
  /**
   * The current path with the locale prefix removed and a trailing slash normalised
   * (`/it/yacht/` → `/yacht`). Route matching, nav modes, scroll resets and the
   * language toggle all key on it, so switching language is not a page change.
   */
  basePath: string;
  copy: Copy;
}

const LangContext = createContext<LangContextValue | null>(null);

/**
 * The URL is the single source of truth for the language — no stored preference and
 * no browser-language detection, so an address always shows the same language to
 * every visitor and crawler (Google: never auto-redirect by language). Must sit INSIDE
 * the router, and outside `<Routes>`: routed elements see the locale-stripped location
 * that App passes to `<Routes location>`, so pages read the language from here, never
 * from `useLocation()`.
 */
export function LangProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { lang, path: basePath } = splitLocale(pathname);

  // Keep <html lang="…"> in sync for client-side navigation (the prerendered pages
  // already ship the right value) so screen readers and `:lang(…)` selectors follow.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<LangContextValue>(
    () => ({ lang, basePath, copy: COPY[lang] }),
    [lang, basePath],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used inside <LangProvider>');
  return ctx;
}
